import React from 'react';
import { getFaIcon } from '@odin-labs/components';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt';
import droneDeploymentMap from './drone-deployment-map.png';

const SolidMapMarkerIcon = getFaIcon({ icon: faMapMarkerAlt });

type PinPosition = { x: number; y: number };

// Simulated coordinate bounds for the jobsite map
const MAP_BOUNDS = {
  latMin: 41.7585,
  latMax: 41.7645,
  lngMin: -87.6835,
  lngMax: -87.6755,
};

function toCoordinates(pin: PinPosition): { lat: string; lng: string } {
  const lat =
    MAP_BOUNDS.latMax - (pin.y / 100) * (MAP_BOUNDS.latMax - MAP_BOUNDS.latMin);
  const lng =
    MAP_BOUNDS.lngMin + (pin.x / 100) * (MAP_BOUNDS.lngMax - MAP_BOUNDS.lngMin);
  return { lat: lat.toFixed(6), lng: lng.toFixed(6) };
}

export function DroneDeploymentMap(props: {
  droneName: string;
  onDeploy: (coordinates: string) => void;
}): React.ReactElement {
  const { onDeploy } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [pin, setPin] = React.useState<PinPosition | null>(null);

  const handleMapClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setPin({ x, y });
    },
    [],
  );

  const handleDeploy = React.useCallback(() => {
    if (pin) {
      const coords = toCoordinates(pin);
      onDeploy(`${coords.lat}, ${coords.lng}`);
    }
  }, [pin, onDeploy]);

  return (
    <div
      ref={containerRef}
      className="odin-relative odin-cursor-crosshair odin-h-full odin-w-full odin-overflow-hidden"
      onClick={handleMapClick}
    >
      <img
        src={droneDeploymentMap}
        alt="Jobsite map"
        className="odin-w-full odin-h-full odin-object-cover"
        draggable={false}
      />
      {pin && (
        <div
          className="odin-absolute odin-pointer-events-none"
          style={{
            left: `${pin.x}%`,
            top: `${pin.y}%`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <SolidMapMarkerIcon className="odin-h-8 odin-w-8 odin-text-red-500 odin-drop-shadow-lg" />
        </div>
      )}

      <div className="odin-absolute odin-bottom-0 odin-left-0 odin-right-0 odin-flex odin-items-center odin-gap-3 odin-px-3 odin-py-2 odin-bg-black/70">
        {pin ? (
          <>
            <button
              type="button"
              className="odin-px-4 odin-py-1.5 odin-bg-purple-600 odin-text-white odin-rounded-md odin-text-sm odin-font-medium hover:odin-bg-purple-700 odin-transition-colors"
              onClick={handleDeploy}
            >
              Deploy drone
            </button>
            <span className="odin-text-xs odin-text-slate-300 odin-font-mono">
              {toCoordinates(pin).lat}, {toCoordinates(pin).lng}
            </span>
          </>
        ) : (
          <span className="odin-text-sm odin-text-white">
            Select drone deployment location
          </span>
        )}
      </div>
    </div>
  );
}
